// DevHub: mobile hamburger menu — below the nav breakpoint, the inline section
// links collapse into a single vertical menu behind a toggle button. Extracted from
// Root.tsx. Mirrors useDropdown's click-outside behaviour and closes on selection.
//
// Admin is deliberately NOT here: it lives in the profile dropdown, which renders at
// every width, so listing it here too would give it two homes.
import type { MouseEvent } from 'react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { commonMessages } from '../../i18n/common';
import { ROUTES } from '../common/constants/routes';
import { useLink } from '../common';
import { useDropdown } from './useDropdown';
import { docsItems, type NavSection } from './navConfig';

export function MobileNavMenu({
  sections: navSections,
  isActivePath,
  isSectionActive,
}: {
  sections: NavSection[];
  isActivePath: (path: string) => boolean;
  isSectionActive: (section: NavSection) => boolean;
}) {
  const link = useLink();
  const { open, setOpen, ref } = useDropdown();
  const { t } = useTranslationRef(commonMessages);

  // Real-anchor nav props (so Cmd/middle-click opens a new tab) that also close
  // the menu on a plain in-app click.
  const go = (path: string) => {
    const l = link(path);
    return {
      href: l.href,
      onClick: (e: MouseEvent) => {
        l.onClick(e);
        setOpen(false);
      },
    };
  };

  return (
    <div className="devhub-mobile-nav" ref={ref}>
      <button
        className={`odx-button odx-button--ghost ${open ? 'is-active' : ''}`}
        type="button"
        onClick={() => setOpen(o => !o)}
        title={t('nav.menu')}
        aria-label={t('nav.menu')}
        aria-expanded={open}
      >
        <odx-icon className="odx-icon" name="core::menu" />
      </button>
      {open && (
        <button
          type="button"
          className="devhub-mobile-nav__scrim"
          aria-label={t('nav.closeMenu')}
          onClick={() => setOpen(false)}
        />
      )}
      {open && (
        <div className="devhub-mobile-nav__menu">
          {navSections.map(section =>
            section.path === ROUTES.overview.docs ? (
              // Docs expands into its jump-off entries (Curated / All Docs).
              <div key={section.path} className="devhub-mobile-nav__group">
                <span className="devhub-mobile-nav__label">{(t as unknown as (k: string) => string)(section.labelKey)}</span>
                {docsItems.map(item => (
                  <a
                    key={item.path}
                    className={`devhub-mobile-nav__item ${isActivePath(item.path) ? 'is-active' : ''}`}
                    {...go(item.path)}
                  >
                    <odx-icon className="odx-icon" name={item.icon} size="sm" />
                    {(t as unknown as (k: string) => string)(item.labelKey)}
                  </a>
                ))}
              </div>
            ) : (
              <a
                key={section.path}
                className={`devhub-mobile-nav__item ${isSectionActive(section) ? 'is-active' : ''}`}
                {...go(section.to ?? section.path)}
              >
                {(t as unknown as (k: string) => string)(section.labelKey)}
              </a>
            ),
          )}
        </div>
      )}
    </div>
  );
}
